import React, { useEffect, useRef, useState } from 'react';
import { jsonEditorStyles } from '../jsonEditorStyles';
import Button from '@mui/material/Button';
import cn from 'clsx';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import InputLabel from '@mui/material/InputLabel';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormLabel from '@material-ui/core/FormLabel';
import FormGroup from '@material-ui/core/FormGroup';
import useComponentUIHook from './useComponentUIHook';
import useJSONEditorHook from '../useJSONEditorHook';
import { SelectComponent, TextComponent, TooltipComponent } from '../../uiComponents';
import { Radio, RadioGroup, SelectChangeEvent } from '@mui/material';
import { OptionProps } from '../../../view-models';
import { getJSONClaims, setJSONClaims } from '../../../store';
import CONSTANTS from '../../../constants/constants';
import DeleteIcon from '@mui/icons-material/Delete';
import ModeEditOutlineSharpIcon from '@mui/icons-material/ModeEditOutlineSharp';
import { cloneDeep } from 'lodash';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import SessionManagement from './sessionManagement';
import jsonComponents from '../../../constants/jsonComponents';
import { useSelector, useDispatch } from 'react-redux';
import { init_UserInputPageComponent } from '../init_components';
import constants from '../../../constants/constants';
import jsonConstants from '../../../constants/jsonConstants';
import TemplatizedFieldComponent, { TemplatizedValue, getTemplatizedValue } from './templatizedFieldComponent';

const styles1 = makeStyles((theme: Theme) =>
  createStyles({
    flexCol: {
      display: 'flex',
      flexDirection: 'column',
    },
    flexColfill: {
      display: 'flex',
      flexDirection: 'column',
      flexBasis: '100%',
      flex: '1',
      padding: '2px',
      overflow: 'hidden',
      '& .inline-tooltip': {
        marginTop: '10px',
      },
    },
    flexRow: {
      display: 'flex',
      flexDirection: 'row',
    },
    attribute: {
      borderBottom: '1px solid grey',
      padding: 10,
    },
    pointer: {
      cursor: 'pointer',
    },
    icon: {
      cursor: 'pointer',
      marginLeft: '0.5rem',
    },
    justifyContent: {
      justifyContent: 'space-between',
    },
    marginRight: { marginRight: '20px' },
    marginLeft: { marginLeft: '20px' },
    displayAttribute: {
      flex: 1,
    },
    actions: {
      width: 90,
    },
  }),
);

interface componentProps {
  componentData: any;
  closeGritComponent: any;
  setUnsavedChanges: any;
}

const GenerateErrorPageUI = ({ componentData, closeGritComponent, setUnsavedChanges }: componentProps) => {
  const { modifiedData, setModifiedData, checkIfSameIgnoringSubFlows, setModifiedTemplatizations, modifiedTemplatizations } = useComponentUIHook(componentData);
  const configData = jsonComponents.errorPage;
  let [claimsList, setClaimsList] = useState<OptionProps[]>([]);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  let [nextItem, setNextItem] = useState<any>({
    sourceType: '',
    source: '',
  });

  const allClaims = useSelector(getJSONClaims);

  const classes = styles1();
  const classes1 = jsonEditorStyles();

  useEffect(() => {
    if (Object.keys(allClaims).length > 0) {
      const _temp = Object.keys(allClaims)
        .filter((clm: any) => {
          return jsonConstants.displayableTypes.includes(allClaims[clm].dataType);
        })
        .reduce((acc: OptionProps[], curVal: string) => acc.concat({ label: curVal, value: curVal }), [])
        .sort((a: OptionProps, b: OptionProps) => (a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1));
      setClaimsList(_temp);
    }
  }, [allClaims]);

  const onDeleteHandler = (index: number) => {
    const _entries = modifiedData['entries'].reduce((acc: any[], item: any, ind: number) => {
      return ind === index ? acc : acc.concat(item);
    }, []);
    setModifiedData({
      ...modifiedData,
      entries: _entries,
    });
  };

  const submitData = (data: any) => {
    setDisableSubmit(false);
    closeGritComponent(data, modifiedTemplatizations, false);
  };

  useEffect(() => {
    const _res: boolean = checkIfSameIgnoringSubFlows([]);
    setDisableSubmit(
      _res ||
        !modifiedData['entries'] ||
        modifiedData['entries'].some((ent: any) => {
          return !(ent['sourceType'] && (ent['sourceType'] === 'literal' || ent['source']));
        }),
    );
    setUnsavedChanges(_res);
  }, [modifiedData, modifiedTemplatizations]);

  return (
    <div>
      <div className={classes1.subHeading}>Error Display</div>
      <div>
        <div className={classes.flexRow}>
          <TemplatizedFieldComponent
            modifiedTemplatizations={modifiedTemplatizations}
            setModifiedTemplatizations={setModifiedTemplatizations}
            data={{
              fieldPath: 'contentDefinition',
              label: configData['contentDefinition']['displayName'],
              style: { margin: 1, width: 600 },
              value: getTemplatizedValue(modifiedData['contentDefinition']),
              variant: 'text'
            }}
            changeHandler={(e: TemplatizedValue) => {
              setModifiedData({ ...modifiedData, contentDefinition: { ...e } });
            }}
          />
        </div>
        {modifiedData['entries'] &&
          modifiedData['entries'].map((ent: any, idx: number) => {
            return (
              <div className={classes.flexRow} key={`entry${idx}`}>
                <SelectComponent
                  changeHandler={(e: SelectChangeEvent<string>) => {
                    const updatedEntries = modifiedData['entries'].slice();
                    updatedEntries[idx] = { sourceType: e.target.value, source: '' };
                    setModifiedData({
                      ...modifiedData,
                      entries: updatedEntries,
                    });
                  }}
                  data={{
                    value: modifiedData['entries'][idx]['sourceType'] ? modifiedData['entries'][idx]['sourceType'] : '',
                    label: 'Data Source',
                    options: [
                      { value: 'literal', label: 'Literal' },
                      { value: 'claim', label: 'Claim' },
                      { value: 'resolver', label: 'Session Context' },
                    ],
                    style: { margin: 1, width: 200 },
                  }}
                />
                {(modifiedData['entries'][idx]['sourceType'] === 'claim' ||
                  modifiedData['entries'][idx]['sourceType'] === 'resolver') && (
                  <SelectComponent
                    changeHandler={(e: SelectChangeEvent<string>) => {
                      const updatedEntries = modifiedData['entries'].slice();
                      updatedEntries[idx] = { sourceType: updatedEntries[idx]['sourceType'], source: e.target.value };
                      setModifiedData({
                        ...modifiedData,
                        entries: updatedEntries,
                      });
                    }}
                    data={{
                      value: modifiedData['entries'][idx]['source'] ? modifiedData['entries'][idx]['source'] : '',
                      label: 'Data Source',
                      options:
                        modifiedData['entries'][idx]['sourceType'] === 'claim' ? claimsList : constants.SESSIONCONTEXT,
                      style: { margin: 1, width: 200 },
                    }}
                  />
                )}
                {modifiedData['entries'][idx]['sourceType'] === 'literal' && (
                  <TextComponent
                    changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                      const updatedEntries = modifiedData['entries'].slice();
                      updatedEntries[idx] = { sourceType: updatedEntries[idx]['sourceType'], source: e.target.value };
                      setModifiedData({
                        ...modifiedData,
                        entries: updatedEntries,
                      });
                    }}
                    data={{
                      value: modifiedData['entries'][idx]['source'] ? modifiedData['entries'][idx]['source'] : '',
                      type: 'string',
                      label: 'Text to Display',
                      style: { margin: 1, width: 450 },
                    }}
                  />
                )}
                <Button
                  variant="contained"
                  sx={{ alignSelf: 'center' }}
                  onClick={() => {
                    onDeleteHandler(idx);
                  }}
                >
                  Delete
                </Button>
              </div>
            );
          })}
        <div className={classes.flexRow}>
          <SelectComponent
            changeHandler={(e: SelectChangeEvent<string>) => {
              setNextItem({ sourceType: e.target.value, source: '' });
            }}
            data={{
              value: nextItem['sourceType'] ? nextItem['sourceType'] : '',
              label: 'Data Source',
              options: [
                { value: 'literal', label: 'Literal' },
                { value: 'claim', label: 'Claim' },
                { value: 'resolver', label: 'Session Context' },
              ],
              style: { margin: 1, width: 200 },
            }}
          />
          {(nextItem['sourceType'] === 'claim' || nextItem['sourceType'] === 'resolver') && (
            <SelectComponent
              changeHandler={(e: SelectChangeEvent<string>) => {
                setNextItem({ ...nextItem, source: e.target.value });
              }}
              data={{
                value: nextItem['source'] ? nextItem['source'] : '',
                label: 'Data Source',
                options: nextItem['sourceType'] === 'claim' ? claimsList : constants.SESSIONCONTEXT,
                style: { margin: 1, width: 200 },
              }}
            />
          )}
          {nextItem['sourceType'] === 'literal' && (
            <TextComponent
              changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                setNextItem({ ...nextItem, source: e.target.value });
              }}
              data={{
                value: nextItem['source'] ? nextItem['source'] : '',
                type: 'string',
                label: 'Text to Display',
                style: { margin: 1, width: 450 },
              }}
            />
          )}
          <Button
            variant="contained"
            sx={{ alignSelf: 'center' }}
            disabled={!(nextItem['sourceType'] && (nextItem['sourceType'] === 'literal' || nextItem['source']))}
            onClick={() => {
              const updatedEntries = modifiedData['entries'] ? modifiedData['entries'].slice() : [];
              updatedEntries.push({ sourceType: nextItem['sourceType'], source: nextItem['source'] });
              setModifiedData({
                ...modifiedData,
                entries: updatedEntries,
              });
              setNextItem({ sourceType: '', source: '' });
            }}
          >
            Add
          </Button>
        </div>
      </div>
      <div>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          onClick={() => {
            submitData(cloneDeep(modifiedData));
          }}
          disabled={disableSubmit}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default GenerateErrorPageUI;
